import { motion } from 'motion/react';
import { useInView } from '../../../components/hooks/useInView.jsx';
// import { useInView } from "../../../components/hooks/useInView.jsx";
import { MapPin, Briefcase, Users, Target, AlertTriangle, TrendingUp } from 'lucide-react';

export function UserPersonas() {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  const personas = [
    {
      name: 'Sarah',
      age: 28,
      level: 'Beginner',
      occupation: 'Marketing Assistant',
      location: 'Urban Employee',
      color: '#ff6b9d',
      avatar: '👩‍💼',
      behaviour: [
        'Recently decided to improve her fitness after years of a sedentary lifestyle',
        'Searches for easy-to-follow workout routines and beginner-friendly plans',
        'Prefers apps that offer guided tutorials and motivational notifications'
      ],
      needs: [
        'Clear guidance on starting a fitness routine',
        'Simple and non-intimidating user interface',
        'Progress tracking to stay motivated'
      ],
      painPoints: [
        'Confused by jargon-heavy fitness content',
        'Difficulty staying consistent due to lack of knowledge',
        'Intimidated by advanced exercises or crowded gyms'
      ],
      goals: [
        'Build a consistent habit of exercising',
        'Lose weight and improve stamina'
      ]
    },
    {
      name: 'Mike',
      age: 35,
      level: 'Intermediate',
      occupation: 'Software Developer',
      location: 'Suburban Employee',
      color: '#4facfe',
      avatar: '👨‍💻',
      behaviour: [
        'Works out 5-6 days a week, alternating between gym sessions and running',
        'Likes tracking performance metrics like pace, heart rate, and calories burned',
        'Shares achievements on social media to connect with other fitness enthusiasts'
      ],
      needs: [
        'Comprehensive workout tracking, including custom routines',
        'Integration with wearables (e.g., smartwatches)',
        'Detailed analytics and progress reports'
      ],
      painPoints: [
        'Frustrated when data doesn\'t sync across devices',
        'Annoyed by apps that lack customization options'
      ],
      goals: [
        'Improve running time and build muscle',
        'Stay engaged with challenging programs and leaderboards'
      ]
    },
    {
      name: 'Layla',
      age: 42,
      level: 'Busy Parent',
      occupation: 'Freelance Designer',
      location: 'Urban Self-Employed',
      color: '#fd5b38',
      avatar: '👩‍🎨',
      behaviour: [
        'Uses fitness apps to find time-efficient workouts between work and family duties',
        'Focused on overall health and maintaining energy',
        'Interested in meal planning alongside exercise'
      ],
      needs: [
        'Short, effective workout plans (e.g., HIIT, yoga)',
        'Suggestions for balanced, easy-to-make meals',
        'Features to track sleep and stress levels'
      ],
      painPoints: [
        'Overwhelmed by apps with too many features',
        'Struggles to maintain consistency during busy weeks'
      ],
      goals: [
        'Feel healthier and more energized',
        'Balance fitness with a busy schedule'
      ]
    }
  ];

  return (
    <section ref={ref} className="py-20 px-6 bg-black/30 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl mb-8">
            User Personas
          </h2>
          <div className="h-1 w-20 bg-[#fd5b38] mx-auto mb-8" />
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
            Understanding our diverse user base to create personalized experiences for every fitness journey
          </p>
        </motion.div>

        {/* Personas Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {personas.map((persona, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 60 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group relative bg-gradient-to-br from-[#1a1a1a] to-black rounded-3xl item-border border-gray-800 hover:border-[#fd5b38] transition-all duration-500 overflow-hidden"
            >
              {/* Glowing background */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-3xl"
                style={{ background: `radial-gradient(circle at top, ${persona.color}20, transparent)` }}
              />

              {/* Card Content */}
              <div className="relative z-10">
                {/* Header */}
                <div className="p-8 pb-6 border-b border-gray-800 group-hover:border-gray-700 transition-colors duration-300">
                  <div className="flex items-start justify-between mb-4">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="text-6xl"
                    >
                      {persona.avatar}
                    </motion.div>
                    <span 
                      className="px-3 py-1 rounded-full text-sm"
                      style={{ 
                        backgroundColor: `${persona.color}20`,
                        color: persona.color,
                        border: `1px solid ${persona.color}50`
                      }}
                    >
                      {persona.level}
                    </span>
                  </div>
                  
                  <h3 className="text-3xl mb-2">{persona.name}</h3>
                  <p className="text-xl text-gray-400 mb-4">{persona.age} years old</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Briefcase className="w-4 h-4" style={{ color: persona.color }} />
                      <span className="text-sm">{persona.occupation}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <MapPin className="w-4 h-4" style={{ color: persona.color }} />
                      <span className="text-sm">{persona.location}</span>
                    </div>
                  </div>
                </div>

                {/* Content Sections */}
                <div className="p-8 space-y-6">
                  {/* Behaviour */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${persona.color}20` }}
                      >
                        <Users className="w-4 h-4" style={{ color: persona.color }} />
                      </div>
                      <h4 className="text-lg" style={{ color: persona.color }}>
                        Behaviour
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {persona.behaviour.map((item, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={isInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ duration: 0.5, delay: index * 0.2 + i * 0.1 }}
                          className="text-sm text-gray-400 flex items-start gap-2 leading-relaxed"
                        >
                          <span 
                            className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
                            style={{ backgroundColor: persona.color }}
                          />
                          <span>{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Needs */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${persona.color}20` }}
                      >
                        <Target className="w-4 h-4" style={{ color: persona.color }} />
                      </div>
                      <h4 className="text-lg" style={{ color: persona.color }}>
                        Needs
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {persona.needs.map((item, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={isInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ duration: 0.5, delay: index * 0.2 + i * 0.1 }}
                          className="text-sm text-gray-400 flex items-start gap-2 leading-relaxed"
                        >
                          <span 
                            className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
                            style={{ backgroundColor: persona.color }}
                          />
                          <span>{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Pain Points */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${persona.color}20` }}
                      >
                        <AlertTriangle className="w-4 h-4" style={{ color: persona.color }} />
                      </div>
                      <h4 className="text-lg" style={{ color: persona.color }}>
                        Pain Points
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {persona.painPoints.map((item, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={isInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ duration: 0.5, delay: index * 0.2 + i * 0.1 }}
                          className="text-sm text-gray-400 flex items-start gap-2 leading-relaxed"
                        >
                          <span 
                            className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
                            style={{ backgroundColor: persona.color }}
                          />
                          <span>{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Goals */}
                  <div className="pt-4 border-t border-gray-800">
                    <div className="flex items-center gap-2 mb-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${persona.color}20` }}
                      >
                        <TrendingUp className="w-4 h-4" style={{ color: persona.color }} />
                      </div>
                      <h4 className="text-lg" style={{ color: persona.color }}>
                        Goals
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {persona.goals.map((item, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={isInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ duration: 0.5, delay: index * 0.2 + i * 0.1 }}
                          className="text-sm text-gray-300 flex items-start gap-2 leading-relaxed group-hover/goal:text-white transition-colors"
                        >
                          <span 
                            className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
                            style={{ backgroundColor: persona.color }}
                          />
                          <span>{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Bottom decoration */}
                <div 
                  className="h-1 w-full"
                  style={{ 
                    background: `linear-gradient(90deg, ${persona.color}, transparent)`
                  }}
                />
              </div>

              {/* Corner glow */}
              <div 
                className="absolute -bottom-20 -right-20 w-60 h-60 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ backgroundColor: `${persona.color}30` }}
              />
            </motion.div>
          ))}
        </div>

        {/* Summary Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="bg-gradient-to-r from-[#fd5b38]/10 via-transparent to-[#fd5b38]/10 p-8 md:p-12 rounded-3xl item-border border-[#fd5b38]/30"
        >
          <h3 className="text-3xl md:text-4xl mb-6 text-center text-[#fd5b38]">
            Design Implications
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#ff6b9d]/30 to-transparent rounded-2xl flex items-center justify-center mx-auto mb-4 item-border border-[#ff6b9d]/30">
                <span className="text-3xl">🎯</span>
              </div>
              <h4 className="text-xl mb-2 text-gray-200">Beginner-Friendly</h4>
              <p className="text-gray-400">Simple onboarding and clear guidance for users new to fitness</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4facfe]/30 to-transparent rounded-2xl flex items-center justify-center mx-auto mb-4 item-border border-[#4facfe]/30">
                <span className="text-3xl">📊</span>
              </div>
              <h4 className="text-xl mb-2 text-gray-200">Advanced Tracking</h4>
              <p className="text-gray-400">Detailed analytics and customization for experienced users</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#fd5b38]/30 to-transparent rounded-2xl flex items-center justify-center mx-auto mb-4 item-border border-[#fd5b38]/30">
                <span className="text-3xl">⚡</span>
              </div>
              <h4 className="text-xl mb-2 text-gray-200">Time-Efficient</h4>
              <p className="text-gray-400">Quick workouts and streamlined features for busy schedules</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
